import React, { useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Table, Tooltip, Tag, Popconfirm, Button, Space, Dropdown, Modal, Input } from 'antd';
import {
  VideoCameraOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
  AudioOutlined,
  EditOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import type { TableProps, TableColumnsType as ColumnsType } from 'antd';
import transcript_pb, { Transcript, TranscriptMetadata } from '@gen/proto/lr/transcript/v1alpha1/transcript_pb';
import { timestampToString, durationToString } from '../helpers';
import { LinkWithToken as Link } from '../LinkWithToken';
import LanguageCode from './LanguageCode';
import { isFeatureEnabled } from '../features';

type FileTypeIconProps = {
  metadata: TranscriptMetadata.AsObject;
};

const FileTypeIcon: React.FC<FileTypeIconProps> = (props) => {
  return props.metadata.fileType === transcript_pb.TranscriptMetadata.FileType.VIDEO ? (
    <VideoCameraOutlined title='Myndband' />
  ) : (
    <AudioOutlined title='Hljóðskrá' />
  );
};

// This function sorts the data locally based on the props
const sortTranscripts = (
  transcripts: transcript_pb.Transcript.AsObject[],
  sortBy: TranscriptsListProps['sortBy'],
  sortDirection: TranscriptsListProps['sortDirection'],
): transcript_pb.Transcript.AsObject[] => {
  const direction = sortDirection === 'ascend' ? 1 : -1;

  // Create a new array to avoid mutating props
  return [...transcripts].sort((a, b) => {
    // Ensure metadata exists
    if (!a.metadata || !b.metadata) return 0;

    switch (sortBy) {
      case 'subject':
        return a.metadata.subject.localeCompare(b.metadata.subject) * direction;

      case 'recordingDuration': {
        const durationA = a.metadata.recordingDuration?.seconds || 0;
        const durationB = b.metadata.recordingDuration?.seconds || 0;
        return (durationA - durationB) * direction;
      }

      case 'versionTime': {
        const timeA = a.version?.creationTime?.seconds || 0;
        const timeB = b.version?.creationTime?.seconds || 0;
        return (timeA - timeB) * direction;
      }

      default:
        return 0;
    }
  });
};

export type TranscriptsListProps = {
  anonymousAccess?: boolean;
  transcripts: transcript_pb.Transcript.AsObject[];
  sortBy: 'subject' | 'versionTime' | 'recordingDuration';
  sortDirection: 'descend' | 'ascend';
  handleSort: (sortBy: TranscriptsListProps['sortBy'], sortDirection: TranscriptsListProps['sortDirection']) => void;
  onClickKeyword?: (keyword: string) => void;
  onDelete: (job: transcript_pb.Transcript.AsObject) => void;
  onRemoveFromCollection?: (job: transcript_pb.Transcript.AsObject) => void;
  onUpdateMetadata?: (name: string, metadata: transcript_pb.TranscriptMetadata.AsObject) => void;
  deleteError?: boolean;
  selectedRowKeys?: React.Key[];
  onSelectionChange?: (selectedRowKeys: React.Key[], selectedRows: transcript_pb.Transcript.AsObject[]) => void;
} & RouteComponentProps;

const ContextMenuRow = (props: any) => {
  const { menuItems, children, className, ...restProps } = props;
  const [open, setOpen] = useState(false);

  React.useEffect(() => {
    return () => {
      // Cleanup to prevent state updates on unmounted component
      setOpen(false);
    };
  }, []);

  if (!menuItems) {
    return (
      <tr className={className} {...restProps}>
        {children}
      </tr>
    );
  }

  // Intercept the click events on the menu items to explicitly close the dropdown
  const handledMenuItems = menuItems.map((item: any) => ({
    ...item,
    onClick: (e: any) => {
      setOpen(false);
      if (item.onClick) {
        item.onClick(e);
      }
    },
  }));

  return (
    <Dropdown menu={{ items: handledMenuItems }} trigger={['contextMenu']} open={open} onOpenChange={setOpen}>
      <tr className={className} {...restProps}>
        {children}
      </tr>
    </Dropdown>
  );
};

const TranscriptsList: React.FC<TranscriptsListProps> = (props) => {
  const {
    transcripts,
    anonymousAccess,
    sortBy,
    sortDirection,
    handleSort,
    onClickKeyword,
    onDelete,
    onRemoveFromCollection,
    onUpdateMetadata,
    selectedRowKeys,
    onSelectionChange,
  } = props;
  const [page, setPage] = useState({ current: 1, perPage: 10 });
  const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
  const [transcriptToRename, setTranscriptToRename] = useState<transcript_pb.Transcript.AsObject | null>(null);
  const [newSubject, setNewSubject] = useState('');

  const [confirmDeleteModalVisible, setConfirmDeleteModalVisible] = useState(false);
  const [transcriptToDelete, setTranscriptToDelete] = useState<transcript_pb.Transcript.AsObject | null>(null);

  const [confirmRemoveModalVisible, setConfirmRemoveModalVisible] = useState(false);
  const [transcriptToRemove, setTranscriptToRemove] = useState<transcript_pb.Transcript.AsObject | null>(null);

  const handleRename = () => {
    if (newSubject.trim() && transcriptToRename && onUpdateMetadata) {
      const updatedMetadata = {
        ...(transcriptToRename.metadata || {}),
        subject: newSubject.trim(),
      };
      onUpdateMetadata(transcriptToRename.name, updatedMetadata as transcript_pb.TranscriptMetadata.AsObject);
      setIsRenameModalVisible(false);
      setTranscriptToRename(null);
      setNewSubject('');
    }
  };

  const showRenameModal = (record: transcript_pb.Transcript.AsObject) => {
    setTranscriptToRename(record);
    setNewSubject(record.metadata?.subject || '');
    setIsRenameModalVisible(true);
  };

  const executeDelete = () => {
    if (transcriptToDelete) {
      onDelete(transcriptToDelete);
      setConfirmDeleteModalVisible(false);
      setTranscriptToDelete(null);
    }
  };

  const executeRemove = () => {
    if (transcriptToRemove && onRemoveFromCollection) {
      onRemoveFromCollection(transcriptToRemove);
      setConfirmRemoveModalVisible(false);
      setTranscriptToRemove(null);
    }
  };

  const rowSelection = onSelectionChange
    ? {
        selectedRowKeys,
        onChange: onSelectionChange,
      }
    : undefined;

  const handleTableChange: TableProps<transcript_pb.Transcript.AsObject>['onChange'] = (
    pagination,
    filters,
    sorter,
  ) => {
    // Check if sorting is what triggered the change
    // Use sorter.columnKey which matches our column `key` prop
    if (!Array.isArray(sorter) && sorter.columnKey) {
      const sortKey = sorter.columnKey as TranscriptsListProps['sortBy'];
      handleSort(sortKey, sorter.order as TranscriptsListProps['sortDirection']);
    }

    if (pagination.current) {
      setPage({
        current: pagination.current,
        perPage: pagination.pageSize ?? 10,
      });
    }
  };

  const columns: ColumnsType<transcript_pb.Transcript.AsObject> = [
    {
      key: 'fileType',
      width: 40,
      align: 'center',
      className: 'fileType',
      render: (_, record) => {
        const href = `/${anonymousAccess ? 'public-' : ''}${record.name}`;
        return (
          <Link to={href}>
            <FileTypeIcon metadata={record.metadata!} />
          </Link>
        );
      },
    },
    {
      title: 'Titill',
      dataIndex: ['metadata', 'subject'],
      key: 'subject',
      width: '40%',
      className: 'subject',
      sorter: true,
      sortOrder: sortBy === 'subject' ? sortDirection : undefined,
      render: (subject: string, record: Transcript.AsObject) => {
        const href = `/${anonymousAccess ? 'public-' : ''}${record.name}`;
        return <Link to={href}>{subject}</Link>;
      },
    },
    {
      title: 'Merki',
      dataIndex: ['metadata', 'keywordsList'],
      key: 'keywords',
      width: '25%',
      className: 'keywords',
      render: (keywordsList: string[]) => (
        <>
          {keywordsList.map((keyword, i) => (
            <Tag
              key={i}
              color='blue'
              onClick={onClickKeyword ? () => onClickKeyword(keyword) : undefined}
              style={{ cursor: onClickKeyword ? 'pointer' : 'default' }}
            >
              {keyword}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: 'Mál',
      dataIndex: ['metadata', 'languageCode'],
      key: 'languageCode',
      width: '10%',
      className: 'languageCode',
      render: (languageCode) => <LanguageCode languageCode={languageCode} />,
    },
    {
      title: (
        <Tooltip title='Lengd'>
          <ClockCircleOutlined />
        </Tooltip>
      ),
      dataIndex: ['metadata', 'recordingDuration'],
      key: 'recordingDuration',
      width: 70,
      align: 'center',
      className: 'recordingDuration',
      sorter: true,
      sortOrder: sortBy === 'recordingDuration' ? sortDirection : undefined,
      render: (duration) => durationToString(duration),
    },
    {
      title: 'Síðast uppfært',
      dataIndex: 'version',
      key: 'versionTime',
      width: '15%',
      className: 'versionTime',
      sorter: true,
      sortOrder: sortBy === 'versionTime' ? sortDirection : 'descend',
      defaultSortOrder: 'descend',
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (version) => (version ? timestampToString(version.creationTime) : null),
    },
    {
      key: 'delete',
      width: 100,
      align: 'center',
      className: 'deleteButtonCell',
      render: (_, record) => (
        <Space>
          {onRemoveFromCollection && (
            <Popconfirm
              title='Ertu viss um að þú viljir fjarlægja úr safni?'
              onConfirm={() => onRemoveFromCollection(record)}
              okText='Já, fjarlægja'
              cancelText='Hætta við'
            >
              <Button type='text' danger icon={<DeleteOutlined />} title='Fjarlægja úr safni' />
            </Popconfirm>
          )}
          {!onRemoveFromCollection && (
            <Popconfirm
              title='Ertu viss um að þú viljir eyða?'
              description='Ekki er hægt að snúa aftur!'
              onConfirm={() => onDelete(record)}
              okText='Já, eyða'
              cancelText='Hætta við'
            >
              <Button type='text' danger icon={<DeleteOutlined />} title='Eyða upptöku' />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const sortedTranscripts = sortTranscripts(transcripts, sortBy, sortDirection);

  const getContextMenuItems = (record: transcript_pb.Transcript.AsObject) => {
    const items = [
      {
        key: 'rename',
        icon: <EditOutlined />,
        label: 'Breyta titli',
        onClick: () => showRenameModal(record),
      },
    ];

    if (onRemoveFromCollection) {
      items.push({
        key: 'remove',
        icon: <LogoutOutlined />,
        danger: true,
        label: 'Fjarlægja úr safni',
        onClick: () => {
          setTranscriptToRemove(record);
          setConfirmRemoveModalVisible(true);
        },
      });
    }

    items.push({
      key: 'delete',
      icon: <DeleteOutlined />,
      danger: true,
      label: 'Eyða',
      onClick: () => {
        setTranscriptToDelete(record);
        setConfirmDeleteModalVisible(true);
      },
    });

    return items;
  };

  const components = React.useMemo(
    () => ({
      body: {
        row: ContextMenuRow,
      },
    }),
    [],
  );

  return (
    <>
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={sortedTranscripts}
        rowKey='name'
        size='small'
        onChange={handleTableChange}
        pagination={page}
        sortDirections={['ascend', 'descend']}
        components={components}
        onRow={(record) => {
          return {
            menuItems: getContextMenuItems(record),
            onContextMenu: (event) => {
              event.preventDefault();
            },
          } as any;
        }}
      />
      <Modal
        title='Breyta titli upptöku'
        open={isRenameModalVisible}
        onOk={handleRename}
        onCancel={() => setIsRenameModalVisible(false)}
        okText='Breyta'
        cancelText='Hætta við'
      >
        <Input
          placeholder='Nýr titill'
          value={newSubject}
          onChange={(e) => setNewSubject(e.target.value)}
          onPressEnter={handleRename}
          autoFocus
        />
      </Modal>

      <Modal
        title='Eyða upptöku'
        open={confirmDeleteModalVisible}
        onOk={executeDelete}
        onCancel={() => setConfirmDeleteModalVisible(false)}
        okText='Já, eyða'
        cancelText='Hætta við'
        okButtonProps={{ danger: true }}
      >
        <p>Ertu viss um að þú viljir eyða þessari upptöku?</p>
        <p>Ekki er hægt að snúa aftur!</p>
      </Modal>

      <Modal
        title='Fjarlægja úr safni'
        open={confirmRemoveModalVisible}
        onOk={executeRemove}
        onCancel={() => setConfirmRemoveModalVisible(false)}
        okText='Já, fjarlægja'
        cancelText='Hætta við'
        okButtonProps={{ danger: true }}
      >
        <p>Ertu viss um að þú viljir fjarlægja þessa upptöku úr safninu?</p>
      </Modal>
    </>
  );
};

export default withRouter(TranscriptsList);
